import React from 'react';
import Tabs, { TabPanel } from './Tabs';
import DataTable from './DataTable';
import FilterBar from './FilterBar';
import EmptyState from './EmptyState';
import ErrorState from './ErrorState';
import Button from './Button';
import Breadcrumbs from '../components/Breadcrumbs';
import useUrlFilters from './useUrlFilters';

/**
 * Layout for CVE, package and host pages: the header stays put and the active
 * tab's table scrolls. Put an EntityHeader (or EntityHeaderSkeleton) and
 * EntityTabs inside.
 */
export default function EntityPage({ children }) {
  return <div className="h-full flex flex-col overflow-hidden px-6 pt-5 pb-5">{children}</div>;
}

// Active tab id from ?tab=, falling back to the first tab for unknown values.
export function useEntityTab(tabIds) {
  const [{ tab }, setValues] = useUrlFilters({ tab: tabIds[0] });
  const active = tabIds.includes(tab) ? tab : tabIds[0];
  return [active, (next) => setValues({ tab: next })];
}

// tabs: [{ id, label, count?, render() }]. Only the active tab renders.
export function EntityTabs({ tabs, value, onChange, label, idPrefix = 'entity' }) {
  const active = tabs.find((t) => t.id === value) || tabs[0];
  return (
    <>
      <Tabs tabs={tabs} value={active.id} onChange={onChange} label={label} idPrefix={idPrefix} />
      <TabPanel id={active.id} idPrefix={idPrefix} className="flex-1 min-h-0 flex flex-col pt-3">
        {active.render()}
      </TabPanel>
    </>
  );
}

/**
 * Filter bar plus server-paginated table for one tab.
 * query: the result of usePagedQuery. Remaining props go to DataTable.
 * filtered: true when any filter is set; picks filteredEmpty over empty.
 * actions: buttons shown at the right end of the filter row (e.g. Export).
 */
export function TabTable({ filters, values, onFiltersChange, query, filtered = false, empty, filteredEmpty, actions, ...tableProps }) {
  return (
    <>
      {(filters || actions) && (
        <div className="shrink-0 mb-3 flex items-start gap-2">
          {filters && <FilterBar className="flex-1 min-w-0" filters={filters} values={values} onChange={onFiltersChange} />}
          {actions && <div className="shrink-0 ml-auto flex items-center gap-2">{actions}</div>}
        </div>
      )}
      <DataTable
        className="flex-1"
        rows={query.rows}
        loading={query.loading}
        error={query.error}
        onRetry={query.retry}
        pagination={query.pagination}
        empty={filtered && filteredEmpty ? filteredEmpty : empty}
        {...tableProps}
      />
    </>
  );
}

export const clearFiltersAction = (onClear) => <Button onClick={onClear}>Clear filters</Button>;

const isNotFound = (error) => error?.status === 404 || error?.status === 400;

// Shown instead of the header and tabs when the entity itself could not be loaded.
export function EntityLoadProblem({ crumbs, noun, identifier, error, onRetry, listPath, listLabel, notFoundText }) {
  return (
    <div className="h-full overflow-y-auto px-6 pt-5 pb-5">
      {crumbs && <Breadcrumbs items={crumbs} className="mb-3" />}
      {isNotFound(error) ? (
        <EmptyState
          className="!px-0"
          title={`No ${noun} ${identifier ? `with ID ${identifier}` : 'here'}`}
          action={<Button to={listPath}>{listLabel}</Button>}
        >
          {notFoundText || `It may have been removed from the inventory, or the link is wrong. Find the ${noun} in the list instead.`}
        </EmptyState>
      ) : (
        <ErrorState className="!px-0" title={`This ${noun} could not be loaded`} error={error} onRetry={onRetry} />
      )}
    </div>
  );
}
